import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { adminFetch, clearAdminToken, getAdminToken } from './adminApi'

function utcTodayString(): string {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}

function defaultFrom(): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 30)
  return d.toISOString().slice(0, 10)
}

type Summary = {
  range: { from: string; to: string }
  users: { totalNotDeleted: number; registeredInRange: number; activeInRange: number }
  profiles: { total: number; byModeration: Record<string, number> }
  photosApprovedTotal: number
  actionsInRange: number
  matchesInRange: number
  messagesInRange: number
  reportsNewOpen: number
}

type DayRow = { day: string; count: number }
type ActionDayRow = { day: string; action: string; count: number }
type ActionTypeRow = { action: string; count: number }
type GenderRow = { gender: string; count: number }

export function AdminDashboard() {
  const navigate = useNavigate()
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(utcTodayString)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [usersByDay, setUsersByDay] = useState<DayRow[]>([])
  const [actionsByDay, setActionsByDay] = useState<ActionDayRow[]>([])
  const [actionsByType, setActionsByType] = useState<ActionTypeRow[]>([])
  const [profilesByGender, setProfilesByGender] = useState<GenderRow[]>([])
  const [matchesByDay, setMatchesByDay] = useState<DayRow[]>([])

  const load = useCallback(async () => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true })
      return
    }
    const q = { from, to }
    setLoading(true)
    setErr(null)
    try {
      const [s, u, ad, at, g, m] = await Promise.all([
        adminFetch<Summary>('/api/admin/stats/summary', q),
        adminFetch<DayRow[]>('/api/admin/stats/users-by-day', q),
        adminFetch<ActionDayRow[]>('/api/admin/stats/actions-by-day', q),
        adminFetch<ActionTypeRow[]>('/api/admin/stats/actions-by-type', q),
        adminFetch<GenderRow[]>('/api/admin/stats/profiles-by-gender', q),
        adminFetch<DayRow[]>('/api/admin/stats/matches-by-day', q),
      ])
      setSummary(s)
      setUsersByDay(u)
      setActionsByDay(ad)
      setActionsByType(at)
      setProfilesByGender(g)
      setMatchesByDay(m)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      if ((e as { status?: number }).status === 401) {
        clearAdminToken()
        navigate('/admin/login', { replace: true })
      }
    } finally {
      setLoading(false)
    }
  }, [from, to, navigate])

  useEffect(() => {
    void load()
  }, [load])

  const logout = () => {
    clearAdminToken()
    navigate('/admin/login', { replace: true })
  }

  const genderRu = (g: string) => (g === 'MALE' ? 'М' : g === 'FEMALE' ? 'Ж' : g)

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/80 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-white">Аналитика</h1>
            <p className="text-xs text-slate-500">ТолькоСвои — админ</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-400">
              с
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-white"
              />
            </label>
            <label className="flex items-center gap-1 text-xs text-slate-400">
              по
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-white"
              />
            </label>
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              Обновить
            </button>
            <button
              type="button"
              onClick={logout}
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
            >
              Выйти
            </button>
            <Link
              to="/admin/users"
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
            >
              Пользователи
            </Link>
            <Link to="/dashboard" className="text-sm text-indigo-400 hover:underline">
              В приложение
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 p-4">
        {err ? (
          <div className="rounded-lg border border-red-900/60 bg-red-950/40 p-4 text-sm text-red-200">{err}</div>
        ) : null}

        {loading && !summary ? (
          <p className="text-slate-400">Загрузка…</p>
        ) : summary ? (
          <>
            <p className="text-xs text-slate-500">
              Период API: {new Date(summary.range.from).toLocaleString('ru-RU')} —{' '}
              {new Date(summary.range.to).toLocaleString('ru-RU')} (UTC)
            </p>

            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['Пользователей всего', String(summary.users.totalNotDeleted)],
                ['Регистраций в периоде', String(summary.users.registeredInRange)],
                ['Активных в периоде', String(summary.users.activeInRange)],
                ['Анкет всего', String(summary.profiles.total)],
                ['Свайпов в периоде', String(summary.actionsInRange)],
                ['Мэтчей в периоде', String(summary.matchesInRange)],
                ['Сообщений в периоде', String(summary.messagesInRange)],
                ['Фото одобрено всего', String(summary.photosApprovedTotal)],
                ['Жалоб (NEW)', String(summary.reportsNewOpen)],
              ].map(([label, val]) => (
                <div
                  key={label}
                  className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 shadow-sm"
                >
                  <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                  <p className="mt-1 text-2xl font-bold text-white">{val}</p>
                </div>
              ))}
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <h2 className="text-sm font-semibold text-slate-300">Модерация анкет</h2>
              <ul className="mt-2 flex flex-wrap gap-3 text-sm">
                {Object.entries(summary.profiles.byModeration).map(([k, v]) => (
                  <li key={k} className="rounded bg-slate-800 px-2 py-1">
                    {k}: <strong className="text-white">{v}</strong>
                  </li>
                ))}
              </ul>
            </section>

            <TwoCol
              title="Регистрации по дням"
              rows={usersByDay}
              empty="Нет данных"
            />
            <TwoCol title="Мэтчи по дням" rows={matchesByDay} empty="Нет данных" />
            <TableSection
              title="Свайпы по дням и типу"
              columns={['День', 'Тип', 'Кол-во']}
              rows={actionsByDay.map((r) => [r.day, r.action, String(r.count)])}
              empty="Нет данных"
            />
            <TableSection
              title="Свайпы за период по типу"
              columns={['Тип', 'Кол-во']}
              rows={actionsByType.map((r) => [r.action, String(r.count)])}
              empty="Нет данных"
            />
            <TableSection
              title="Анкеты по полу (обновление анкеты в периоде)"
              columns={['Пол', 'Кол-во']}
              rows={profilesByGender.map((r) => [genderRu(r.gender), String(r.count)])}
              empty="Нет данных"
            />
          </>
        ) : null}
      </main>
    </div>
  )
}

function TwoCol({
  title,
  rows,
  empty,
}: {
  title: string
  rows: DayRow[]
  empty: string
}) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="text-sm font-semibold text-slate-300">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">{empty}</p>
      ) : (
        <div className="mt-3 max-h-64 space-y-1 overflow-y-auto text-sm">
          {rows.map((r) => (
            <div key={r.day} className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-slate-400">{r.day}</span>
              <div className="h-2 min-w-0 flex-1 rounded-full bg-slate-800">
                <div
                  className="h-2 rounded-full bg-indigo-500"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
              <span className="w-10 text-right font-mono text-slate-200">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function TableSection({
  title,
  columns,
  rows,
  empty,
}: {
  title: string
  columns: string[]
  rows: string[][]
  empty: string
}) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="text-sm font-semibold text-slate-300">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">{empty}</p>
      ) : (
        <div className="mt-3 max-h-72 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-900 text-xs uppercase text-slate-500">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="border-b border-slate-800 px-2 py-2 font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/80 hover:bg-slate-800/30">
                  {row.map((cell, j) => (
                    <td key={j} className="px-2 py-1.5 font-mono text-slate-200">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
